import { createQueryKeys } from '@lukemorales/query-key-factory';
import type { StoxlyClient } from '@stoxly/api-client';
import type { Body, Query } from '../op-types';

export function createCompanyProfileQueries(client: StoxlyClient) {
  return createQueryKeys('companyProfiles', {
    // ['companyProfiles', 'bySymbol', symbol]
    bySymbol: (symbol: string) => ({
      queryKey: [symbol],
      queryFn: ({ signal }) => client.companyProfilesGetProfile({ params: { symbol }, signal }),
    }),

    /**
     * ['companyProfiles', 'display', symbol] — the READ-ONLY display slice.
     *
     * Distinct from `bySymbol` on purpose: that one hits the SWR envelope, which
     * ENQUEUES a paid deep-research run on a miss. This one only ever reads, so it
     * is the query a product page may run on every render.
     */
    display: (symbol: string) => ({
      queryKey: [symbol],
      queryFn: ({ signal }) =>
        client.companyProfilesGetProfileDisplay({ params: { symbol }, signal }),
    }),

    // ['companyProfiles', 'runs', { query }]
    runs: (query?: Query<'companyProfilesAdminListRuns'>) => ({
      queryKey: [{ query }],
      queryFn: ({ signal }) => client.companyProfilesAdminListRuns({ queries: query, signal }),
    }),

    // ['companyProfiles', 'profiles', { query }] — latest ready profile per symbol
    profiles: (query?: Query<'companyProfilesAdminListProfiles'>) => ({
      queryKey: [{ query }],
      queryFn: ({ signal }) => client.companyProfilesAdminListProfiles({ queries: query, signal }),
    }),

    // ['companyProfiles', 'run', id]
    run: (id: string) => ({
      queryKey: [id],
      queryFn: ({ signal }) => client.companyProfilesAdminGetRun({ params: { id }, signal }),
    }),

    // ['companyProfiles', 'topHeld', { query }]
    topHeld: (query?: Query<'companyProfilesAdminTopHeld'>) => ({
      queryKey: [{ query }],
      queryFn: ({ signal }) => client.companyProfilesAdminTopHeld({ queries: query, signal }),
    }),

    // ['companyProfiles', 'topMarketCap', { query }]
    topMarketCap: (query?: Query<'companyProfilesAdminTopMarketCap'>) => ({
      queryKey: [{ query }],
      queryFn: ({ signal }) => client.companyProfilesAdminTopMarketCap({ queries: query, signal }),
    }),
  });
}

export function createCompanyProfileMutations(client: StoxlyClient) {
  return {
    enqueueBatch: {
      mutationFn: (body: Body<'companyProfilesAdminEnqueueBatch'>) =>
        client.companyProfilesAdminEnqueueBatch(body),
    },
    retryRun: {
      mutationFn: ({ id }: { id: string }) =>
        client.companyProfilesAdminRetryRun(undefined, { params: { id } }),
    },
    // The product's "generate this profile" button — paid, once per company (server-guarded).
    requestProfile: {
      mutationFn: ({ symbol }: { symbol: string }) =>
        client.companyProfilesRequestProfile(undefined, { params: { symbol } }),
    },
  };
}
